/* eslint-disable no-alert */
import React from 'react'
import { Box, Container, LinearProgress } from '@mui/material'
import { ErrorBoundary, FallbackProps } from 'react-error-boundary'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router-dom'

import { useAuth0, User } from '@auth0/auth0-react'
import { PeriodicBackgroundTasks } from '@containers/main/main-idle-tasks'
import { claims, onChangePermissions, pendoSetup } from '@containers/main/main-utils'
import { useApi } from '@core/api'
import type { SelfUser } from '@core/api/generated'
import { isAuthRejection } from '@core/auth/is-auth-rejection'
import { routeMap } from '@core/constants/routes'
import env from '@core/env'
import { Customer, useMainState } from '@core/main-state'
import { addSnack } from '@core/snack/snack-state'
import locationUtils from '@core/utils/location'
import { useSuspenseQuery } from '@tanstack/react-query'

declare global {
  interface Window {
    pendo: any
    Cypress: any
  }
}

type AuthGuardProps = {
  children: React.ReactNode
}

let pendoInitialized = false

const getTokenUserRole = (user: User | undefined): SelfUser['role'] => {
  const allowedRoles = user?.[claims]?.['x-hasura-allowed-roles'] || []

  if (!allowedRoles.length) {
    console.warn('User without allowed roles', user?.email, user?.[claims])
  }

  return allowedRoles.includes('customer_admin') ? 'Admin' : 'User'
}

const SyncBffUser = () => {
  const api = useApi()
  const { user: auth0User, getAccessTokenSilently } = useAuth0()
  const { t } = useTranslation('common')

  useSuspenseQuery({
    queryKey: ['bffUser'],
    queryFn: async () => {
      const { data } = await api.getSelfUser({ throwOnError: true })

      // TODO: drop this whole token-vs-BFF role reconciliation block once Hasura
      // is fully removed — at that point roles live only in the BFF and the token
      // is no longer a source of truth, so prompts/alerts/forced reloads here
      // become unnecessary.
      const tokenRole = getTokenUserRole(auth0User)
      const mismatchTokenRole = tokenRole !== data.user.role

      if (mismatchTokenRole) {
        console.warn(
          `User role in token (${tokenRole}) does not match user role from BFF (${data.user.role}). This might cause permission issues.`,
          { user: data.user, tokenRole },
        )

        await getAccessTokenSilently({ cacheMode: 'off' }).catch((error) => {
          console.error('Error while refreshing access token after role mismatch detected', {
            error,
          })
        })

        let shouldReload = false

        // lost admin
        if (tokenRole === 'Admin' && data.user.role === 'User') {
          window.alert(t('role_changed.reload'))
          shouldReload = true
        }

        // gained admin
        if (tokenRole === 'User' && data.user.role === 'Admin') {
          if (document.hidden) {
            shouldReload = true
            addSnack({
              message: t('role_changed.reloading'),
              severity: 'info',
            })
          } else {
            shouldReload = window.confirm(t('role_changed.reload'))
          }
        }

        if (shouldReload) {
          useMainState.getState().reset()
          setTimeout(() => locationUtils.reload(), 0)
          return data
        }
      }

      const { user: prevUser, customer: prevCustomer } = useMainState.getState()

      if (prevUser) {
        const roleChanged = prevUser.role !== data.user.role
        const customerChanged = prevCustomer?.id !== data.customer.id

        if (roleChanged) {
          console.info('User role changed, reloading the page to apply new permissions', {
            from: prevUser.role,
            to: data.user.role,
          })
          onChangePermissions()
        }

        if (customerChanged) {
          console.info('User customer changed, reloading the page to apply new customer features', {
            from: prevCustomer?.id,
            to: data.customer.id,
          })
          onChangePermissions()
        }
      }

      const customer = data.customer as Customer

      useMainState.setState({ user: data.user, customer })

      if (!pendoInitialized && env.PENDO_ENABLED && !window.Cypress) {
        pendoInitialized = true
        pendoSetup(data.user, customer)
      }

      return data
    },
    retry: false,
    // refetch errors are intentionally NOT thrown to the ErrorBoundary: the
    // user is mid-work with valid user/customer from the first call, and a
    // transient blip on the 120s poll shouldn't surface an error page. Real
    // revocation will land on the next mutation. AuthErrorBoundary covers the
    // cold-start failure only.
    // every 120 seconds, to keep the session alive and get updated user info
    // also useful for detecting when the user role/customer has changed
    refetchInterval: 120 * 1000,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
  })

  return null
}

const ProgressBar = () => {
  const { t } = useTranslation('common', { keyPrefix: 'app_loader' })
  return <LinearProgress data-testid="loader" aria-label={t('aria_loader')} color="secondary" />
}

const AuthGuard = (props: AuthGuardProps) => {
  const userSynced = useMainState((state) => Boolean(state.user))

  if (!userSynced) {
    console.debug('User not synced yet, showing loader...')
    return <ProgressBar />
  }

  return props.children
}

const AuthErrorBoundary = ({ error }: FallbackProps) => {
  const { user: auth0User } = useAuth0()
  const { t } = useTranslation('legacy', { keyPrefix: 'errors' })

  React.useEffect(() => {
    // Warn from the effect, not the render body: the fallback re-renders, and a
    // render-body warn re-fired on every one of them — that was the console burst.
    console.warn('Error in AuthGuard', error)
  }, [error])

  if (isAuthRejection(error)) {
    // The response interceptor that threw this error already drove the secure
    // single-flight logout (the /v2/logout redirect tears the SPA down). The
    // boundary only holds the loader until that redirect commits — it does not
    // log out itself, so there is no second, racing logout.
    return <ProgressBar />
  }

  const raw =
    (error as { body?: { message?: string }; response?: { data?: { message?: string } } })?.body
      ?.message ??
    (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
    (error as { message?: string })?.message ??
    'unknown'

  return (
    <Container sx={{ py: 2 }}>
      <Box mt={5}>
        <Box typography="body2" color="primary.light">
          <i>{auth0User?.email}</i>
        </Box>
        <Box typography="h2" my={2}>
          {t('unable_to_connect')}
        </Box>
        <Box typography="body1" sx={{ maxWidth: { xs: '100%', sm: '60%' } }}>
          {t('fatal_error')}
        </Box>
        <Box typography="caption" my={5}>
          {raw}
        </Box>
      </Box>
    </Container>
  )
}

const AuthGuardAuthenticatedGate = (props: AuthGuardProps) => {
  const { isAuthenticated, error, isLoading, logout } = useAuth0()

  // A cached id token keeps isAuthenticated=true while the refresh token is
  // dead; Auth0 then surfaces the failure on `error`. No BFF request is made on
  // this path, so no interceptor will drive the logout — the gate owns it. This
  // is mutually exclusive with the interceptor logouts (the children, and thus
  // SyncBffUser, never mount once we log out here), so it needs no single-flight.
  const sessionRejected = isAuthRejection(error)

  React.useEffect(() => {
    if (sessionRejected) {
      console.warn('Auth rejected on session restore, logging out', error)
      logout({
        logoutParams: { returnTo: `${window.location.origin}?auth0ErrorParam=expired` },
      })
    }
  }, [sessionRejected, logout, error])

  if (isLoading || sessionRejected) {
    return <ProgressBar />
  }

  if (!isAuthenticated) {
    return <Navigate to={routeMap.LOGIN_ROUTE} />
  }

  if (error) {
    // A non-auth error (e.g. provider misconfiguration) — surface the fatal page.
    throw error
  }

  return props.children
}

const AuthGuardWrapper = (props: AuthGuardProps) => {
  return (
    <ErrorBoundary FallbackComponent={AuthErrorBoundary}>
      <React.Suspense fallback={<ProgressBar />}>
        <AuthGuardAuthenticatedGate>
          <SyncBffUser />
          <PeriodicBackgroundTasks />
          <AuthGuard>{props.children}</AuthGuard>
        </AuthGuardAuthenticatedGate>
      </React.Suspense>
    </ErrorBoundary>
  )
}

export default AuthGuardWrapper
